import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { checkCallAPI } from '../../api';
import { toast } from 'sonner';

const yesterdayCentral = () => {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(Date.now() - 86400000));
  return parts;
};

const RESULT_STYLE = {
  missed: 'bg-[var(--tms-error)] text-white',
  late: 'bg-[var(--tms-warning)]/20 text-[var(--tms-warning)] border border-[var(--tms-warning)]/40',
};

// Supervisor report: required check-call tasks missed / completed late on a Central date, grouped by operator
export const MissedReportDialog = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [date, setDate] = useState(yesterdayCentral());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    checkCallAPI.missedReport(date)
      .then(r => { setData(r.data); setExpanded({}); })
      .catch(() => toast.error('Failed to load report'))
      .finally(() => setLoading(false));
  }, [open, date]);

  const t = data?.totals;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="missed-report-modal">
        <DialogHeader>
          <DialogTitle>Missed Check Calls Report</DialogTitle>
          <DialogDescription className="text-xs">Required tasks not completed, or completed after their deadline, per operator (Central date)</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-3 text-sm">
          <label className="text-xs text-[var(--tms-text-muted)]">Date</label>
          <input type="date" className="border border-[var(--tms-border)] rounded px-2 h-8 text-sm bg-transparent" value={date} onChange={e => setDate(e.target.value)} data-testid="missed-report-date" />
          <button className="text-xs underline text-[var(--tms-text-muted)]" onClick={() => setDate(yesterdayCentral())}>Yesterday</button>
          {t && (
            <div className="ml-auto flex gap-2 text-xs" data-testid="missed-report-totals">
              <span className="px-2 py-1 rounded bg-[var(--tms-hover)]">{t.tasks} tasks</span>
              <span className="px-2 py-1 rounded bg-[var(--tms-error)] text-white font-semibold">{t.missed} missed</span>
              <span className="px-2 py-1 rounded bg-[var(--tms-warning)]/20 text-[var(--tms-warning)] font-semibold">{t.late} late</span>
              <span className="px-2 py-1 rounded bg-[var(--tms-success)]/15 text-[var(--tms-success)] font-semibold">{t.on_time} on time</span>
            </div>
          )}
        </div>
        <div className="max-h-[60vh] overflow-y-auto space-y-2 mt-2">
          {loading && <p className="text-sm text-[var(--tms-text-muted)]">Loading…</p>}
          {!loading && data && data.operators.length === 0 && <p className="text-sm text-[var(--tms-text-muted)] py-6 text-center">No required check calls on this date.</p>}
          {!loading && data?.operators.map(g => {
            const problems = g.missed + g.late;
            const isOpen = expanded[g.operator_id || 'unassigned'] ?? problems > 0;
            return (
              <div key={g.operator_id || 'unassigned'} className="border border-[var(--tms-border)] rounded-lg" data-testid={`missed-report-operator-${g.operator_id || 'unassigned'}`}>
                <button
                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-[var(--tms-hover)]"
                  onClick={() => setExpanded(e => ({ ...e, [g.operator_id || 'unassigned']: !isOpen }))}
                >
                  <span className="font-semibold text-sm text-[var(--tms-text)]">{g.operator_name}</span>
                  <span className="flex gap-2 text-xs">
                    <span className="text-[var(--tms-text-muted)]">{g.tasks} tasks</span>
                    {g.missed > 0 && <span className="px-2 rounded-full bg-[var(--tms-error)] text-white font-bold">{g.missed} missed</span>}
                    {g.late > 0 && <span className="px-2 rounded-full bg-[var(--tms-warning)]/20 text-[var(--tms-warning)] font-bold">{g.late} late</span>}
                    {problems === 0 && <span className="px-2 rounded-full bg-[var(--tms-success)]/15 text-[var(--tms-success)] font-bold">all on time</span>}
                  </span>
                </button>
                {isOpen && g.items.length > 0 && (
                  <table className="w-full text-xs border-t border-[var(--tms-border)]">
                    <thead className="text-[var(--tms-text-muted)]">
                      <tr><th className="text-left px-3 py-1">Load</th><th className="text-left px-3 py-1">Task</th><th className="text-left px-3 py-1">Stop</th><th className="text-left px-3 py-1">Due</th><th className="text-left px-3 py-1">Result</th></tr>
                    </thead>
                    <tbody>
                      {g.items.map(i => (
                        <tr key={i.key} className="border-t border-[var(--tms-border)]/60" data-testid={`missed-report-item-${i.key}`}>
                          <td className="px-3 py-1.5 font-mono font-semibold">
                            <button className="hover:underline" onClick={() => { onOpenChange(false); navigate(`/loads/${i.load_id}`); }}>{i.load_number || i.load_id.slice(0, 8)}</button>
                            <div className="font-sans font-normal text-[var(--tms-text-muted)] truncate max-w-[160px]">{i.customer_name}</div>
                          </td>
                          <td className="px-3 py-1.5">{i.task_label}</td>
                          <td className="px-3 py-1.5">{i.facility_name ? <>{i.facility_name}<div className="text-[var(--tms-text-muted)]">{[i.city, i.state].filter(Boolean).join(', ')}</div></> : '—'}</td>
                          <td className="px-3 py-1.5 whitespace-nowrap">{i.due_central_display}</td>
                          <td className="px-3 py-1.5">
                            <span className={`px-2 py-0.5 rounded-full font-bold uppercase text-[10px] ${RESULT_STYLE[i.result]}`}>{i.result}</span>
                            {i.result === 'late' && <div className="text-[var(--tms-text-muted)] mt-0.5">{i.completed_at_display} • {i.completed_by_name}</div>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
